import React, { useEffect, useState } from "react";
import { usePulpayConfig } from "../hooks/usePulpayConfig";
import {
  ArrowRight,
  Check,
  Clock,
  ShieldCheck,
  Wallet,
  Zap,
  FileCheck,
  Smartphone,
  Percent,
  Users,
  Star,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Send,
  BadgeCheck,
  Sparkles,
  User,
  CheckCircle2,
} from "lucide-react";
import Manat from "../components/Manat";

const TYPE_PHRASES = [
  "Pulpay var!",
  "Sürətli həll!",
  "15 dəqiqədə pul!",
  "Ani təsdiq!",
  "Rəsmi gəlir yox!",
];

function useTypewriter(phrases, typingSpeed = 90, deletingSpeed = 45, pauseDuration = 1400) {
  const [text, setText] = useState("");
  const [idx, setIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[idx % phrases.length];
    let timeout;
    if (!deleting) {
      if (text.length < current.length) {
        timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), typingSpeed);
      } else {
        timeout = setTimeout(() => setDeleting(true), pauseDuration);
      }
    } else {
      if (text.length > 0) {
        timeout = setTimeout(() => setText(current.slice(0, text.length - 1)), deletingSpeed);
      } else {
        setDeleting(false);
        setIdx((n) => (n + 1) % phrases.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, idx, phrases, typingSpeed, deletingSpeed, pauseDuration]);

  return text;
}

const TITLE_PHRASE_1 = "RƏSMİ GƏLİR İSTƏNİLMİR";
const TITLE_PHRASE_2 = "GECİKMƏYƏ BAXILMIR";

function useLoopType(fullText, speed = 90, holdMs = 1800) {
  const [t, setT] = useState("");
  const [del, setDel] = useState(false);
  useEffect(() => {
    let tid;
    if (!del) {
      if (t.length < fullText.length) tid = setTimeout(() => setT(fullText.slice(0, t.length + 1)), speed);
      else tid = setTimeout(() => setDel(true), holdMs);
    } else {
      if (t.length > 0) tid = setTimeout(() => setT(fullText.slice(0, t.length - 1)), speed / 2);
      else tid = setTimeout(() => setDel(false), 400);
    }
    return () => clearTimeout(tid);
  }, [t, del, fullText, speed, holdMs]);
  return t;
}

/**
 * Syncronised dual-line typewriter — types both lines at the same time
 * and clears both at the same time. Uses the longer line as the driver.
 */
function useDualLoopType(lineA, lineB, speed = 85, holdMs = 2200) {
  const maxLen = Math.max(lineA.length, lineB.length);
  const [n, setN] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    let tid;
    if (!del) {
      if (n < maxLen) tid = setTimeout(() => setN((v) => v + 1), speed);
      else tid = setTimeout(() => setDel(true), holdMs);
    } else {
      if (n > 0) tid = setTimeout(() => setN((v) => v - 1), speed / 2);
      else tid = setTimeout(() => setDel(false), 500);
    }
    return () => clearTimeout(tid);
  }, [n, del, maxLen, speed, holdMs]);
  return [lineA.slice(0, Math.min(n, lineA.length)), lineB.slice(0, Math.min(n, lineB.length))];
}

function useFaqTypewriter(text, open, speed = 15) {
  const [t, setT] = useState("");
  useEffect(() => {
    if (!open) {
      setT("");
      return;
    }
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setT(text.slice(0, i));
      if (i >= text.length) clearInterval(iv);
    }, speed);
    return () => clearInterval(iv);
  }, [text, open, speed]);
  return t;
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  const typed = useFaqTypewriter(a, open);
  return (
    <div className={`pp-faq-item ${open ? "pp-faq-open" : ""}`}>
      <button className="pp-faq-summary" onClick={() => setOpen((v) => !v)}>
        <span className="pp-faq-num pp-faq-num-logo">
          <img src="/assets/logo.png" alt="" />
          <span className="pp-faq-num-txt">{String(idx + 1).padStart(2, "0")}</span>
        </span>
        <span className="pp-faq-q">{q}</span>
        <span className="pp-faq-ic-wrap">
          <ChevronDown size={18} className="pp-faq-ic" />
        </span>
      </button>
      {open && (
        <div className="pp-faq-a">
          {typed}
          <span className="pp-faq-caret" />
        </div>
      )}
    </div>
  );
}

export default function Splash({ onStart, notify }) {
  const [t1Anim, t2Anim] = useDualLoopType(TITLE_PHRASE_1, TITLE_PHRASE_2, 85, 2400);
  const [infoModal, setInfoModal] = useState(null);
  const config = usePulpayConfig();
  const contact = config.contact || {};
  const heroStatic = !!(config.splash && config.splash.hero_static);
  const t1 = heroStatic ? TITLE_PHRASE_1 : t1Anim;
  const t2 = heroStatic ? TITLE_PHRASE_2 : t2Anim;
  const handleAppComingSoon = (e) => {
    e.preventDefault();
    if (typeof notify === "function") {
      notify("Tətbiqimiz yaxın zamanda yüklənəcək!");
    }
  };
  const openInfo = (e, key) => {
    e.preventDefault();
    setInfoModal(key);
  };
  const closeInfo = () => setInfoModal(null);
  return (
    <div className="landing">
      {/* NAVBAR */}
      <header className="l-nav">
        <div className="l-container l-nav-inner">
          <a href="#top" className="l-brand">
            <img src="/assets/logo.png" alt="PULPAY" />
            <div>
              <div className="l-brand-name">PULPAY</div>
              <div className="l-brand-sub">KREDİT SİSTEMİ</div>
            </div>
          </a>
          <nav className="l-menu">
            <a href="#features">Xidmətlər</a>
            <a href="#how">Necə işləyir</a>
            <a href="#faq">FAQ</a>
            <a href="#contact">Əlaqə</a>
          </nav>
        </div>
      </header>

      {/* HERO - Brand Design (matches PULPAY mockup) */}
      <section className="pp-hero" id="top">
        {/* Decorative curves */}
        <svg className="pp-curve pp-curve-tl" viewBox="0 0 600 300" preserveAspectRatio="none">
          <path d="M0,0 Q300,220 600,60 L600,0 Z" fill="rgba(59,130,246,0.18)" />
        </svg>
        <svg className="pp-curve pp-curve-tr" viewBox="0 0 600 300" preserveAspectRatio="none">
          <path d="M600,0 Q300,260 0,80 L0,0 Z" fill="rgba(96,165,250,0.14)" />
        </svg>
        <svg className="pp-curve pp-curve-bl" viewBox="0 0 600 240" preserveAspectRatio="none">
          <path d="M0,240 Q300,80 600,180 L600,240 Z" fill="rgba(96,165,250,0.10)" />
        </svg>
        <svg className="pp-curve pp-curve-br" viewBox="0 0 600 240" preserveAspectRatio="none">
          <path d="M600,240 Q300,20 0,160 L0,240 Z" fill="rgba(59,130,246,0.12)" />
        </svg>

        <div className="pp-container">
          {/* Hero photo - premium bank image (replaces old brand header) */}
          <div className="pp-hero-photo" data-testid="hero-photo">
            <img
              src="/assets/hero-bank.webp"
              alt="PULPAY Kredit — pul köçürməsi"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="pp-hero-grid">
            {/* Left: Copy + Features + CTA */}
            <div className="pp-hero-copy">
              <h1 className={`pp-title pp-title-dual${heroStatic ? " pp-title-static" : ""}`}>
                <span className="pp-title-line">
                  <span className="pp-typewriter-text">{t1}</span>
                  {!heroStatic && <span className="pp-typewriter-caret" />}
                </span>
                <span className="pp-title-line pp-title-line-2">
                  <span className="pp-typewriter-text">{t2}</span>
                  {!heroStatic && <span className="pp-typewriter-caret" />}
                </span>
              </h1>
              <p className="pp-subtitle">
                Sizin üçün daha əlçatan<br />kredit imkanları
              </p>

              <div className="pp-feats">
                <div className="pp-feat">
                  <div className="pp-feat-circle">
                    <ShieldCheck size={22} strokeWidth={2.4} />
                  </div>
                  <div className="pp-feat-text">
                    Rəsmi gəlir<br />tələb olunmur
                  </div>
                </div>
                <div className="pp-feat">
                  <div className="pp-feat-circle">
                    <Users size={22} strokeWidth={2.4} />
                  </div>
                  <div className="pp-feat-text">
                    Gecikməniz olsa<br />da mümkündür
                  </div>
                </div>
                <div className="pp-feat">
                  <div className="pp-feat-circle">
                    <Zap size={22} strokeWidth={2.4} />
                  </div>
                  <div className="pp-feat-text">
                    Tam onlayn<br />müraciət
                  </div>
                </div>
              </div>

              <button className="pp-cta" onClick={onStart}>
                Başla <ArrowRight size={18} strokeWidth={3} />
              </button>
            </div>

            {/* Right: Phone + Card */}
            <div className="pp-hero-scene">

              <div className="pp-stage">
                {/* soft halo */}
                <div className="pp-halo" />

                {/* Phone — v3 clean premium design */}
                <div className="ppx-phone" data-testid="hero-phone">
                  <div className="ppx-phone-glow" />
                  <div className="ppx-phone-frame">
                    <div className="ppx-phone-notch">
                      <span className="ppx-phone-cam" />
                      <span className="ppx-phone-speaker" />
                    </div>
                    <div className="ppx-phone-btn ppx-phone-btn-vol-a" />
                    <div className="ppx-phone-btn ppx-phone-btn-vol-b" />
                    <div className="ppx-phone-btn ppx-phone-btn-power" />

                    <div className="ppx-phone-screen">
                      <div className="ppx-phone-topbar">
                        <div className="ppx-phone-brand">
                          <img src="/assets/logo.png" alt="" />
                          <span>PULPAY</span>
                        </div>
                      </div>

                      <div className="ppx-phone-hello">
                        <span className="ppx-phone-hello-hi">Xoş gəldiniz ✨</span>
                        <h4>Kredit limitiniz</h4>
                      </div>

                      <div className="ppx-phone-card">
                        <div className="ppx-phone-card-bg" />
                        <div className="ppx-phone-card-amount">
                          4.000 <em>₼</em>
                        </div>
                        <button className="ppx-phone-card-cta" type="button">
                          Kredit al
                          <ArrowRight size={13} strokeWidth={3} />
                        </button>
                      </div>

                      <div className="ppx-phone-quick">
                        <div className="ppx-phone-q">
                          <span className="ppx-phone-q-ic ppx-q-cyan"><Wallet size={15} strokeWidth={2.4} /></span>
                          <b>Balans</b>
                        </div>
                        <div className="ppx-phone-q">
                          <span className="ppx-phone-q-ic ppx-q-blue"><CreditCard size={15} strokeWidth={2.4} /></span>
                          <b>Kartlar</b>
                        </div>
                        <div className="ppx-phone-q">
                          <span className="ppx-phone-q-ic ppx-q-green"><FileCheck size={15} strokeWidth={2.4} /></span>
                          <b>Ödə</b>
                        </div>
                        <div className="ppx-phone-q">
                          <span className="ppx-phone-q-ic ppx-q-violet"><Percent size={15} strokeWidth={2.4} /></span>
                          <b>Faiz</b>
                        </div>
                      </div>

                      <div className="ppx-phone-tx">
                        <div className="ppx-phone-tx-head">
                          <span>Son əməliyyatlar</span>
                          <span className="ppx-phone-tx-more">Hamısı</span>
                        </div>
                        <div className="ppx-phone-tx-item">
                          <span className="ppx-phone-tx-ic ppx-q-green"><CheckCircle2 size={13} strokeWidth={2.6} /></span>
                          <div className="ppx-phone-tx-body">
                            <b>Kredit təsdiqləndi</b>
                            <em>Bu gün, 14:22</em>
                          </div>
                          <span className="ppx-phone-tx-val">+2 400 ₼</span>
                        </div>
                        <div className="ppx-phone-tx-item">
                          <span className="ppx-phone-tx-ic ppx-q-blue"><CreditCard size={13} strokeWidth={2.6} /></span>
                          <div className="ppx-phone-tx-body">
                            <b>Aylıq ödəniş</b>
                            <em>Dünən, 09:10</em>
                          </div>
                          <span className="ppx-phone-tx-val ppx-tx-out">−185 ₼</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cards image (user-uploaded) — below phone, both visible */}
                <div className="pp-cards-photo" data-testid="hero-cards-photo">
                  <img src="/assets/pulpay-cards.png" alt="Pulpay kartlar" />
                </div>

                {/* Podium base */}
                <div className="pp-podium">
                  <div className="pp-podium-top" />
                  <div className="pp-podium-side" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ - Dynamic super design with typewriter on click */}
      <section className="pp-faq pp-faq-v2" id="faq">
        <div className="pp-faq-glow" />
        <div className="l-container pp-faq-wrap">
          <div className="l-section-head pp-head">
            <span className="l-eyebrow pp-eyebrow">SUAL-CAVAB</span>
            <h2>Ən çox verilən suallar</h2>
            <p className="pp-faq-hint">Suala klikləyin — cavab canlı yazılacaq</p>
          </div>
          <div className="pp-faq-list">
            {FAQ.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA - moved directly below FAQ */}
      <section className="pp-cta-band">
        <div className="pp-cta-orbs">
          <span className="pp-cta-orb pp-cta-orb-1" />
          <span className="pp-cta-orb pp-cta-orb-2" />
          <span className="pp-cta-orb pp-cta-orb-3" />
        </div>
        <div className="pp-cta-marquee">
          <div className="pp-cta-track">
            <span>PULPAY</span><span>·</span>
            <span>15 dəqiqədə təsdiq</span><span>·</span>
            <span>Kart hesabına dərhal</span><span>·</span>
            <span>Rəsmi gəlir tələb yoxdur</span><span>·</span>
            <span>PULPAY</span><span>·</span>
            <span>15 dəqiqədə təsdiq</span><span>·</span>
            <span>Kart hesabına dərhal</span><span>·</span>
            <span>Rəsmi gəlir tələb yoxdur</span><span>·</span>
          </div>
        </div>
        <div className="l-container pp-cta-inner">
          <div className="pp-cta-txt">
            <span className="pp-cta-badge">İNDİ MÜRACİƏT EDİN</span>
            <h2>15 dəqiqədə cavab, dərhal <span className="pp-accent">kart hesabınıza</span></h2>
            <p>Nömrənizi daxil edin və kreditinizi bir neçə addımda alın.</p>
          </div>
          <button className="pp-cta pp-cta-large" onClick={onStart}>
            İndi başla <ArrowRight size={20} strokeWidth={3} />
          </button>
        </div>
      </section>

      {/* FOOTER - Super new design */}
      <footer className="pp-footer" id="contact">
        <div className="pp-footer-topline" />
        <div className="l-container">
          <div className="pp-footer-hero">
            <div className="pp-footer-brand">
              <img src="/assets/logo.png" alt="PULPAY" />
              <div>
                <div className="pp-footer-brand-name">PULPAY</div>
                <div className="pp-footer-brand-sub">KREDİT SİSTEMİ</div>
              </div>
            </div>
            <p className="pp-footer-tag">
              Onlayn kredit platforması — hər zaman yanınızda.
              <br />Rəsmi gəlir tələb olunmur, sürətli təsdiq, təhlükəsiz köçürmə.
            </p>
          </div>

          <div className="pp-footer-grid">
            <div className="pp-footer-col">
              <h4>Şirkət</h4>
              <a href="#" onClick={(e) => openInfo(e, "services")} data-testid="footer-link-services">Xidmətlər</a>
              <a href="#" onClick={(e) => openInfo(e, "how")} data-testid="footer-link-how">Necə işləyir</a>
              <a href="#faq" data-testid="footer-link-faq">FAQ</a>
            </div>
            <div className="pp-footer-col">
              <h4>Hüquqi</h4>
              <a href="#" onClick={(e) => openInfo(e, "terms")} data-testid="footer-link-terms">İstifadə şərtləri</a>
              <a href="#" onClick={(e) => openInfo(e, "privacy")} data-testid="footer-link-privacy">Məxfilik siyasəti</a>
              <a href="#" onClick={(e) => openInfo(e, "credit")} data-testid="footer-link-credit">Kredit şərtləri</a>
              <a href="#" onClick={(e) => openInfo(e, "license")} data-testid="footer-link-license">Lisenziya</a>
            </div>
            <div className="pp-footer-col">
              <h4>Əlaqə</h4>
              {contact.phone_enabled !== false && (
                <span className="pp-footer-link" data-testid="footer-phone">
                  <Phone size={14} /> {contact.phone || "+994 12 123 45 67"}
                </span>
              )}
              {contact.email_enabled !== false && (
                <span className="pp-footer-link" data-testid="footer-email">
                  <Mail size={14} /> {contact.email || "info@pulpay.az"}
                </span>
              )}
              {contact.address_enabled !== false && (
                <span className="pp-footer-link" data-testid="footer-address">
                  <MapPin size={14} /> {contact.address || "Bakı, Azərbaycan"}
                </span>
              )}
            </div>
            <div className="pp-footer-col pp-footer-app">
              <a
                href="https://play.google.com/store"
                onClick={handleAppComingSoon}
                className="pp-store pp-store-google"
                data-testid="footer-google-play-btn"
              >
                <svg className="pp-store-ic" width="26" height="26" viewBox="0 0 512 512" aria-hidden="true">
                  <path fill="#00D2FF" d="M325.3 234.3L104.6 13l280.8 161z"/>
                  <path fill="#00E38C" d="M104.6 13c-3.7 1.9-6.6 5.7-6.6 11.2v463.6c0 5.5 2.9 9.3 6.6 11.2l220.7-234.7L104.6 13z"/>
                  <path fill="#FFD400" d="M385.4 174l-60.1 60.3 60.1 61.2 72.5-41.8c19.9-11.5 19.9-40.4 0-51.9L385.4 174z"/>
                  <path fill="#FF3A44" d="M104.6 499c3.5 1.8 7.9 1.6 12.7-1.2l268.1-154.5-60.1-61.2L104.6 499z"/>
                </svg>
                <span>
                  <small>Tətbiqi yüklə</small>
                  <b>Google Play</b>
                </span>
              </a>
              <a
                href="https://www.apple.com/app-store/"
                onClick={handleAppComingSoon}
                className="pp-store pp-store-apple"
                data-testid="footer-app-store-btn"
              >
                <svg className="pp-store-ic" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#fff" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>
                  <small>Tətbiqi yüklə</small>
                  <b>App Store</b>
                </span>
              </a>
            </div>
          </div>

          <div className="pp-footer-bottom">
            <span className="pp-footer-copy" data-testid="footer-copy">
              <img src="/assets/logo.png" alt="" className="pp-footer-copy-logo" />
              © 2026 PULPAY MMC. Bütün hüquqlar qorunur.
            </span>
          </div>
        </div>
      </footer>
      {infoModal && (
        <InfoModal item={INFO_CONTENT[infoModal]} onClose={closeInfo} />
      )}
    </div>
  );
}

const FAQ = [
  {
    q: "Kredit almaq üçün hansı sənədlər lazımdır?",
    a: "Yalnız şəxsiyyət vəsiqəsi və FIN kod kifayətdir. Rəsmi gəlir arayışı və ya iş yerindən sənəd tələb olunmur.",
  },
  {
    q: "Kredit nə qədər müddətə təsdiqlənir?",
    a: "Müraciətinizdən sonra sistemimiz məlumatları avtomatik yoxlayır və ən çoxu 15 dəqiqə ərzində cavab bildirilir.",
  },
  {
    q: "Gecikmə tarixçəm varsa müraciət edə bilərəmmi?",
    a: "Bəli. PULPAY digər kredit təşkilatlarından fərqli olaraq keçmiş gecikmələrə görə imtina etmir. Hər müraciət ayrıca dəyərləndirilir.",
  },
  {
    q: "Pul nə zaman kart hesabıma gələcək?",
    a: "Kredit təsdiqləndikdən dərhal sonra — adətən 1-5 dəqiqə ərzində — istənilən Azərbaycan bank kartına köçürülür.",
  },
  {
    q: "Faiz dərəcəsi nə qədərdir?",
    a: "İllik faiz dərəcəmiz 10%-dən başlayır. Bütün şərtlər əvvəlcədən açıq və şəffaf göstərilir.",
  },
  {
    q: "Krediti vaxtından əvvəl bağlaya bilərəmmi?",
    a: "Bəli, istənilən vaxt heç bir cərimə tətbiq edilmədən krediti tam və ya qismən bağlaya bilərsiniz.",
  },
];

const INFO_CONTENT = {
  services: {
    title: "Xidmətlərimiz",
    body: [
      "PULPAY sizə tam onlayn kredit xidməti təqdim edir.",
      "• 15 dəqiqə ərzində təsdiq",
      "• Kart hesabına dərhal köçürmə",
      "• Rəsmi gəlir arayışı tələb olunmur",
      "• Yalnız FIN kod və şəxsiyyət vəsiqəsi kifayətdir",
      "• 24/7 dəstək xidməti",
    ],
  },
  how: {
    title: "Necə işləyir?",
    body: [
      "1. Qeydiyyat — Nömrənizi daxil edin və 30 saniyədə hesab yaradın.",
      "2. Müraciət — Kredit məbləği və müddətini seçin, sənədlərinizi göndərin.",
      "3. Pulun alınması — Təsdiqdən sonra pul kart hesabınıza saniyələr içində köçürülür.",
    ],
  },
  terms: {
    title: "İstifadə şərtləri",
    body: [
      "PULPAY platformasından istifadə edərək aşağıdakı şərtləri qəbul edirsiniz:",
      "• 18 yaşınız tamam olmalıdır",
      "• Azərbaycan Respublikasının vətəndaşı olmalısınız",
      "• Verdiyiniz məlumatlar doğru və yenilənmiş olmalıdır",
      "• Kredit öhdəliklərini razılaşdırılmış müddətdə yerinə yetirməlisiniz",
      "Şirkət istənilən vaxt şərtləri yeniləmək hüququnu özündə saxlayır.",
    ],
  },
  privacy: {
    title: "Məxfilik siyasəti",
    body: [
      "Şəxsi məlumatlarınızın qorunması bizim üçün prioritetdir.",
      "• Bütün məlumatlar 256-bit SSL şifrələmə ilə qorunur",
      "• Məlumatlarınız üçüncü tərəflərlə paylaşılmır",
      "• Yalnız kredit qərarı üçün zəruri məlumatlar toplanır",
      "• İstənilən vaxt hesabınızın silinməsini tələb edə bilərsiniz",
    ],
  },
  credit: {
    title: "Kredit şərtləri",
    body: [
      "• Kredit məbləği: 100 ₼ – 10,000 ₼",
      "• Müddət: 1 ay – 24 ay",
      "• İllik faiz dərəcəsi: 10%-dən başlayır",
      "• Ödənişlər aylıq bərabər hissələrlə həyata keçirilir",
      "• Vaxtından əvvəl bağlama zamanı cərimə yoxdur",
      "Bütün şərtlər müqavilə imzalanmadan öncə açıq göstərilir.",
    ],
  },
  license: {
    title: "Lisenziya",
    body: [
      "PULPAY MMC Azərbaycan Respublikasının qanunvericiliyinə uyğun fəaliyyət göstərir.",
      "• Lisenziya №: AZ-KRD-2024-1187",
      "• Sertifikat №: CBA-FSC-0043721",
      "• VÖEN: 1804258951",
      "• Fəaliyyət lisenziyası: Maliyyə Bazarlarına Nəzarət Palatası tərəfindən verilmişdir.",
      "Rəsmi sənədlərin surətini info@pulpay.az ünvanı vasitəsilə əldə edə bilərsiniz.",
    ],
  },
};

function InfoModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!item) return null;
  return (
    <div className="pp-info-overlay" onClick={onClose} data-testid="info-modal-overlay">
      <div className="pp-info-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="info-modal">
        <button className="pp-info-close" onClick={onClose} aria-label="Bağla" data-testid="info-modal-close">×</button>
        <div className="pp-info-icon">
          <img src="/assets/logo.png" alt="" />
        </div>
        <h3 className="pp-info-title" data-testid="info-modal-title">{item.title}</h3>
        <div className="pp-info-body">
          {item.body.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
        <button className="pp-info-ok" onClick={onClose} data-testid="info-modal-ok">Anladım</button>
      </div>
    </div>
  );
}
